import { usePickerChain } from "./PickerChainContext";
import { usePickerQuery, usePickerRoot } from "./PickerContext";
import { containsSelectedValue, type PickerRowGroup } from "./PickerTree";
import { leadsToMatch } from "./PickerTreeQuery";
import type { PickerFlyoutAutoOpen, PickerItemValue } from "./Picker.types";

/**
 * The flyout row a level opens by itself under `flyoutAutoOpen="path"`, as a
 * pure decision. A non-empty top query wins: the first flyout row that
 * matches or leads to a match. With an empty query the selection drives it,
 * as the tree expands a selected value's ancestors: the first flyout row
 * that leads to a selected value (walking the selection in order, since a
 * selected top-level parent reveals nothing by itself). `undefined` when
 * nothing should open, which also closes a level that was auto-opened; a
 * closed popup never opens anything, since its rows may still be mounted
 * while it closes.
 */
export const resolveAutoOpenValue = <T extends PickerItemValue>({
    groups,
    mode,
    isPopupOpen,
    topQuery,
    selectedValues,
}: {
    groups: ReadonlyArray<PickerRowGroup<T>>;
    mode: PickerFlyoutAutoOpen;
    isPopupOpen: boolean;
    topQuery: string;
    selectedValues: readonly PickerItemValue[];
}): T | undefined => {
    if (mode !== "path" || !isPopupOpen) {
        return undefined;
    }
    const flyoutRows = groups
        .flatMap((group) => group.rows)
        .filter((row) => row.isFlyout);
    const query = topQuery.trim();
    if (query !== "") {
        return flyoutRows.find((row) => leadsToMatch(row.item, query))?.item
            .value;
    }
    for (const selected of selectedValues) {
        const selectedSet = new Set<PickerItemValue>([selected]);
        const row = flyoutRows.find((candidate) =>
            containsSelectedValue(candidate.item, selectedSet),
        );
        if (row !== undefined) {
            return row.item.value;
        }
    }
    return undefined;
};

/** `resolveAutoOpenValue` fed from the enclosing root, chain and query contexts. */
export const usePickerAutoOpenValue = <T extends PickerItemValue>(
    groups: ReadonlyArray<PickerRowGroup<T>>,
): T | undefined => {
    const root = usePickerRoot();
    const { topQuery } = usePickerQuery();
    const chain = usePickerChain();
    return resolveAutoOpenValue({
        groups,
        mode: chain?.flyoutAutoOpen ?? "none",
        isPopupOpen: root.isPopupOpen,
        topQuery,
        selectedValues: chain?.selectedValues ?? root.selectedValues,
    });
};
